import { t } from "@comact/crc";
// eslint-disable-next-line no-restricted-imports
import { IMenuItem } from "@comact/crc/core/menu";
import _ from "lodash";
import { ITemplate, getEditableProperties } from "../nodeTemplate/model";
import { IShift } from "../shifts";
import { ILink } from "./links/model";

type IExecutionConfiguration = {
    machineType: IMachineCodecNode["machine"]; // model
    scanners: string[]; // indexes
};

interface INodeMetadata<N = INode> {
    readonly reportedMetadata: Record<keyof IReportedProperties<N>, { lastUpdated: number; }> & { lastUpdated: number; };
    readonly desiredMetadata: Partial<INodeMetadata<N>["reportedMetadata"]>;
}

type ICreateNode<T extends ITemplate["name"], P extends {}> =
    Omit<INodeServerAbstract<T>, "reported"> &
    INodeWellKnownReportedProperties<T> &
    P &
    { desired: IDesiredProperties<P>; } &
    INodeMetadata<P> &
    { childrenIds?: string[]; };

type INodeState =
    "HEALTHY" | "STARTED_HEALTHY" | "STARTED_NOT_HEALTHY" | "CREATED" | "STARTING" | "STOPPING" | "RESTARTING" | "STOPPED" | "ERROR" | "UPDATING" | "DELETING" | "UNREACHABLE" // IEP states
    | "DISCONNECTED" | "UNKNOWN" | "NOT_RUNNING" | "START" | "RUNNING" | "STOP" | "BACKUP" | "UPDATE" | "RESTORE" | "UNAVAILABLE" | "WARNING" | "FAULT" | "READY"; // ICP (CMOC and Cloud) statess
// and more
type IConnectionState = "Disconnected" | "Connected";
interface INodeServerAbstract<T extends ITemplate["name"]> {
    readonly id: string;
    readonly reported: {
        name: string;
        parentId: string;
        templateId: string;
        templateName: T;
        templateVersion: string;
        order?: number;
        deviceId?: string;
        nodeState: INodeState;
        connectionState: IConnectionState;
    };
    desired: Partial<INodeServerAbstract<T>["reported"]>;
    readonly reportedMetadata: Record<keyof INodeServerAbstract<T>["reported"], { lastUpdated: number; }> & { lastUpdated: number; };
    readonly desiredMetadata: Partial<INodeServerAbstract<T>["reportedMetadata"]>;
    readonly creationDate: number;
    readonly modificationDate: number;
}

type INodeBasicProperties<T extends ITemplate["name"]> = Pick<INodeServerAbstract<T>, "id" | "reportedMetadata" | "desiredMetadata" | "creationDate" | "modificationDate">;

type INodeWellKnownReportedProperties<T extends ITemplate["name"]> = INodeServerAbstract<T>["reported"];

interface INodeAbstract<T extends ITemplate["name"]> extends Omit<INodeServerAbstract<T>, "reported">, INodeWellKnownReportedProperties<T> { childrenIds?: string[]; }

export type IReportedProperties<N = INode> = Omit<N, "id" | "creationDate" | "modificationDate" | "reportedMetadata" | "desired" | "desiredMetadata">;
export type IDesiredProperties<N = INode> = Partial<IReportedProperties<N>>;

export const getNodeStateInTransition = (nodeState: INodeState) => (
    (["CREATED", "RESTARTING", "STOP", "START", "STARTING", "STOPPING", "BACKUP", "UPDATE", "UPDATING", "RESTORE"] as INodeState[]).includes(nodeState)
);

const NodeStateStylesWithPriority = {
    error: 1,
    warn: 2,
    success: 3,
    ok: 4,
    unknown: 5,
} as const;

export type INodeStateStyle = keyof typeof NodeStateStylesWithPriority;

export const getNodeStateStyle = (nodeState: INodeState): INodeStateStyle => {
    if ((["ERROR", "FAULT"] as INodeState[]).includes(nodeState)) return "error";
    if ((["RESTARTING",
        "STOP",
        "START",
        "STARTING",
        "STOPPING",
        "BACKUP",
        "UPDATE",
        "UPDATING",
        "RESTORE",
        "STARTED_NOT_HEALTHY",
        "WARNING",
        "UNREACHABLE"] as INodeState[]).includes(nodeState)) {
        return "warn";
    }
    if ((["HEALTHY", "STARTED_HEALTHY", "READY", "RUNNING"] as INodeState[]).includes(nodeState)) return "success";
    if ((["CREATED", "STOPPED", "UNAVAILABLE"] as INodeState[]).includes(nodeState)) return "ok";
    return "unknown";
};

export const machineModels = [
    "BUCKING",
    "DDM",
    "LINEDGER",
    "LOGSORTER",
    "OLI",
    "TBL",
    "ENDOGGING",
    "OFFSET",
    "REDUCER",
    "SAMPLER",
    "VISIONALONE",
    "EDGER",
    "EDGEXPERT",
    "GRADEXPERT",
    "TRIMEXPERT",
    "TRIMMER",
    "PLC",
    "SORTPRO",
] as const;

export type IMachinesModel = typeof machineModels[number];

export const getScannersForIndex = (machineExecutionConfiguration: IExecutionConfiguration) => {
    if (!machineExecutionConfiguration.machineType || _.isEmpty(machineExecutionConfiguration.scanners)) return t("machines.name.all");
    return _.map(machineExecutionConfiguration.scanners, (scannerId) => (
        t([`scanners.${machineExecutionConfiguration.machineType}.${scannerId}`, scannerId])
    )).join(", ");
};

export interface IPlcComponentNode extends INodeAbstract<"plcComponent"> {
    downtimeEnable?: boolean;
}

export interface IMachineCodecNode extends INodeAbstract<"machineCodec"> {
    acquisitionMinDate: number;
    liveConfigs: string;
    simulationConfigs: string;
    productionStatus: {
        machineId: string;
        state: "RUNNING" | "RUNNING_SHIFT_IN_PROGRESS" | "BREAK" | "DOWN" | "IDLE" | "UNKNOWN";
        description: string | null;
        downtimeDescription: string | null;
        shiftId: string | null;
        breakId: string | null;
        downTimeId: string | null;
        lastStateChange: number | null;
    };
    machine: IMachinesModel;
    host: string; // FIXME: missing from server (ICP),
    minGap: number;
    maxGap: number;
    recommendedGap: number;
    version: string;
    downtimeDetectionTime: number;
    cmocIdentifier: string;
    links?: ILink[];
    diagnosticEnabled?: boolean;
    sorterEnabled?: boolean;
    machineInfo?: { // There is a lot more fields send by the server, but these are the only one use for the front end
        interfaceInfo?: {
            metadataOverwrites?: string[];
        };
    };
    desired: IDesiredProperties<IMachineCodecNode>;
}

export const isLinearMachine = (machineModel: IMachinesModel): boolean => {
    const linearList: IMachinesModel[] = ["BUCKING", "DDM", "LINEDGER", "LOGSORTER", "OLI", "TBL"];
    return linearList.includes(machineModel);
};
export type IIepNode = ICreateNode<"iep", {
    host: string;
    version: string;
    cmocIdentifier: string;
    links?: ILink[];
}>;

export type IScannerCodecNode = ICreateNode<"scannerCodec", {
    machine: IMachineCodecNode["machine"];
    scannerIndex: string;
    links?: ILink[];
}>;

export type IMillNode = ICreateNode<"millCodec", {
    shift?: IShift; // (ICP only)
    links?: ILink[];
}>;

export type ICameraNode = ICreateNode<"camera", {
    audioEnabled: boolean;
    internalCameraId: string;
    status: string;
}>;

export type ICameraServerNode = ICreateNode<"cameraServer", {
    host: string;
    username: string;
    password: string;
}>;

export type IStandaloneCameraNode = ICreateNode<"standaloneCamera", {
    host: string;
    username: string;
    password: string;
    model: string;
    captureActive: boolean;
    displayMode: "HLS Stream" | "Direct Stream";
}>;

export type IUnmanagedNode = ICreateNode<
    Exclude<INodeAbstract<null>["templateName"], "millCodec" | "iep" | "machineCodec" | "scannerCodec" | "camera" | "cameraServer" | "standaloneCamera" | "pickAndPlace">,
    {}
>;

export type ISmartVisionZoneNode = ICreateNode<"smartVisionZone", {
    host: string;
    port: number;
}>;

export type IPickAndPlaceNode = ICreateNode<"pickAndPlace", {
    host: string;
    port: number;
}>;

export type ICustomerNode = ICreateNode<"customer", {}>;
export type ILocationNode = ICreateNode<"location", {}>;
export type IL5XNode = ICreateNode<"L5X", {}>;
export type IMachineComponentNode = ICreateNode<"machineComponent", {}>;
export type IGenericDeviceNode = ICreateNode<"genericDevice", {
    downtimeComponent: boolean;
    downtimeEnable?: boolean;
}>;

export type INode = (IMachineCodecNode |
    IIepNode |
    IScannerCodecNode |
    IMillNode |
    ICameraNode |
    ICameraServerNode |
    IStandaloneCameraNode |
    IUnmanagedNode |
    ISmartVisionZoneNode |
    ICustomerNode |
    IPickAndPlaceNode |
    ILocationNode |
    IL5XNode |
    IMachineComponentNode |
    IGenericDeviceNode |
    IPlcComponentNode) & IBranchStatusProperty;

export type INodeWithLinks = IMachineCodecNode | IScannerCodecNode | IMillNode;

export const isNodeWithLinks = (node: INode): node is INodeWithLinks => ( // Type guard to check if it's a Node with links
    (["machineCodec", "scannerCodec", "millCodec", "iep", "genericDevice"] as INode["templateName"][]).includes(node?.templateName)
);

export type IDetailedNodes = { [id: string]: INode; };

export interface INodeHierarchical extends IBasicNode {
}

export interface INodeHierarchicalWithMenu extends INodeHierarchical {
    menu: IMenuItem[];
}

export const createNewNode = (partialData: Partial<INode>, template: ITemplate = null): INode => ({
    id: null,
    desired: template
        ? {
            ..._(getEditableProperties(template))
                .mapValues(({ defaultValue }) => defaultValue)
                .value(),
            ...partialData?.desired,
        }
        : {
            ...splitNodesProperties(partialData).reported,
            ...partialData?.desired,
        },
    creationDate: Date.now(),
    modificationDate: Date.now(),
    ...partialData,
} as INode);

export const splitNodesProperties = <N extends Partial<INode> | INode>(
    { id, creationDate, modificationDate, desired, reportedMetadata, desiredMetadata, ...reported }: N
) => {
    const basic: INodeBasicProperties<N["templateName"]> = { id, creationDate, modificationDate, reportedMetadata, desiredMetadata };
    return {
        basic,
        reported: reported as IReportedProperties<N>,
        desired: desired as IDesiredProperties<N>,
    };
};

export interface INodeServer extends Omit<INodeAbstract<INode["templateName"]>, keyof INodeWellKnownReportedProperties<INode["templateName"]>> {
    reported: INodeWellKnownReportedProperties<INode["templateName"]>;
    desired: Partial<INodeWellKnownReportedProperties<INode["templateName"]>>;
}
export interface IBasicNode extends IBranchStatusProperty {
    // Server props
    id?: string;
    name?: string;
    parentId?: string;
    templateId?: string;
    templateName?: ITemplate["name"];
    templateVersion?: string;
    iepId?: string;
    deviceId?: string;
    modificationDate?: number;
    order: number;
    connectionState?: string;
    nodeState?: INodeState;
    downtimeEnable: boolean;
    downtimeComponent: boolean;

    // Additional props, set in the WUI
    childrenIds?: string[];
}

export type IBasicNodes = Record<string, IBasicNode>;

export const isOutdatedDesiredProperty = <N extends INode>(node: N, property: string): boolean => (
    !_.isEmpty(node.desired?.[property as keyof typeof node.desired]) && (
        (node.reportedMetadata?.[property as keyof typeof node.reportedMetadata] as { lastUpdated: number; })?.lastUpdated >
        (node.desiredMetadata?.[property as keyof typeof node.desiredMetadata] as { lastUpdated: number; })?.lastUpdated
    )
);

export const convertNodeForEdit = <N extends INode>(node: N, template: ITemplate): N => (
    convertNodeForEditWithSpecificProperties(node, _.keys(getEditableProperties(template)) as (keyof IDesiredProperties<N>)[])
);

export const convertNodeForEditWithSpecificProperties = <N extends INode>(node: N, keys: (keyof IDesiredProperties<N>)[]): N => {
    const { basic, reported, desired } = splitNodesProperties(node);

    const newDesired = _.reduce(keys, (prev, key) => {
        prev[key] = _.isNil(desired[key]) || isOutdatedDesiredProperty(node, key as string) ? reported[key] : desired[key];
        return prev;
    }, {} as IDesiredProperties<N>);

    return {
        ...basic,
        ...reported,
        desired: newDesired,
    } as N;
};

export const convertNodeFromServer = (nodeServer: INodeServer): INode => {
    const { desired, reported, ...node } = nodeServer;
    if (_.isEmpty(reported)) { // Node is being create now
        const tweakDesired: IDesiredProperties = { ...desired, nodeState: "CREATED" }; // We add something to the name to differenciate it

        const newNode = {
            ...node,
            desired: tweakDesired,
            ...tweakDesired, // use the desired as reported when creating
            reportedMetadata: _.mapValues(desired, () => ({ lastUpdated: 0 })),
        } as INode;
        return newNode;
    } else {
        return { ...node, desired, ...reported } as INode;
    }
};

/**
 * Statuses conditions of "branches" of nodes, for each condition, it will be the worst possible condition found on that branch.
 */
interface IBranchStatusProperty {
    branchStatuses?: IBranchStatuses;
}
export interface IBranchStatuses {
    worstNodeStatusIncludingNode: INodeStateStyle;
    worstChildNodeStatus: INodeStateStyle;
    worstConnectionStatusIncludingNode: IConnectionStatusState;
    worstChildConnectionStatus?: IConnectionStatusState;
}

export const getConnectionStateForNode = (node: { templateName?: ITemplate["name"]; connectionState?: string; }): IConnectionStatusState => {
    if (node.templateName == "location" || node.templateName == "customer") return "CONNECTED";
    return node.connectionState?.toUpperCase() as IConnectionStatusState;
};

export const convertNodesFromServer = (nodes: INodeServer[]): IDetailedNodes => {
    const convertedNodes = _(nodes).map(convertNodeFromServer).keyBy(({ id }) => id).value();

    return normalizeHierarchyAndStatus(convertedNodes);
};

export const normalizeHierarchyAndStatus = <T extends IBasicNode | INode>(nodes: Record<string, T>): Record<string, T> => {
    _.chain(nodes)
        .map(({ id, parentId }) => ({ id, parentId }))
        .groupBy(({ parentId }) => parentId)
        .mapValues((childNodes) => new Set(_.map(childNodes, ({ id }) => id)))
        .forEach((groupOfChildrenIds, nodeId) => { if (nodes[nodeId]) nodes[nodeId].childrenIds = [...groupOfChildrenIds]; })
        .value();

    // Get the worst branch statuses for nodes.
    const evaluateBranchStatusOf = ({ nodeId }: { nodeId: string; }): IBranchStatuses => {
        const node = nodes[nodeId];
        if (!node) {
            return {
                worstNodeStatusIncludingNode: "unknown",
                worstChildNodeStatus: "unknown",
                worstConnectionStatusIncludingNode: "DISCONNECTED",
                worstChildConnectionStatus: "DISCONNECTED",
            };
        }

        if (node.branchStatuses) return node.branchStatuses; // We have already computed the branch status for this node

        const nodeStatus: IBranchStatuses = {
            worstNodeStatusIncludingNode: getNodeStateStyle(node.nodeState),
            worstChildNodeStatus: "unknown",
            worstConnectionStatusIncludingNode: getConnectionStateForNode(node),
            worstChildConnectionStatus: null,
        };

        if (_.isEmpty(node.childrenIds)) {
            node.branchStatuses = nodeStatus;
            return nodeStatus;
        }

        const childrenBranchStatuses = _.map(node.childrenIds, (childId) => evaluateBranchStatusOf({ nodeId: childId }));
        nodeStatus.worstChildNodeStatus = pickWorstStatus(_.map(childrenBranchStatuses, ({ worstNodeStatusIncludingNode }) => worstNodeStatusIncludingNode));
        nodeStatus.worstNodeStatusIncludingNode = pickWorstStatus([nodeStatus.worstNodeStatusIncludingNode, nodeStatus.worstChildNodeStatus]);
        nodeStatus.worstChildConnectionStatus = pickWorstConnectionStatus(_.map(childrenBranchStatuses, ({ worstConnectionStatusIncludingNode }) => worstConnectionStatusIncludingNode));
        nodeStatus.worstConnectionStatusIncludingNode = pickWorstConnectionStatus([nodeStatus.worstConnectionStatusIncludingNode, nodeStatus.worstChildConnectionStatus]);

        node.branchStatuses = { ...nodeStatus };
        return node.branchStatuses;
    };

    // Add branch statuses to all nodes
    for (const node of _.values(nodes)) {
        evaluateBranchStatusOf({ nodeId: node.id });
    }

    return nodes;
};

const pickWorstConnectionStatus = (statuses: IConnectionStatusState[]): IConnectionStatusState => _.minBy(statuses, (status) => ConnectionStatusesWithPriority[status]);

const pickWorstStatus = (statuses: INodeStateStyle[]): INodeStateStyle => _.minBy(statuses, (status) => NodeStateStylesWithPriority[status]);

export const convertNodeToServer = (node: INode): INodeServer => {
    const { basic, reported, desired } = splitNodesProperties(node);
    return ({ ...basic, reported, desired });
};
export const convertNodesToServer = (nodes: IDetailedNodes | INode[]): INodeServer[] => _.map(nodes, convertNodeToServer);

// Logger Management
export interface ILoggerSetting {
    uniqueKey: string;
    configuredLevel: "OFF" | "ERROR" | "WARN" | "INFO" | "DEBUG" | "TRACE" | null;
    effectiveLevel: ILoggerSetting["configuredLevel"];
}

// Connection states are based on the iot hub device connection state https://learn.microsoft.com/en-us/azure/iot-hub/monitor-device-connection-state#device-twin-connectionstate
const ConnectionStatusesWithPriority = {
    DISCONNECTED: 1,
    CONNECTED: 2,
} as const;

export type IConnectionStatusState = keyof typeof ConnectionStatusesWithPriority;
export const getLinksForCurrentPlatform = (links: ILink[]): ILink[] => (
    process.env.EXEC_MODE == "cmoc"
        ? _.filter(links, (l) => l.displayOn == "prem" || l.displayOn == "both")
        : _.filter(links, (l) => l.displayOn == "cloud" || l.displayOn == "both")
);

// This should be a bit faster than instantiating arrays and sub-string;
const extractNodeId = (nodeId: string, depth: number) => {
    if (!nodeId) return null;

    // Last index is pushed further after each iteration, except for the last segment...
    const indices = _.reduce(nodeId, (acc, char, idx) => {
        if (char == "-") acc.push(idx);
        return acc;
    }, []);

    if (indices.length > depth) return nodeId.substring(0, indices[depth]);
    if (indices.length == depth) return nodeId;

    return null;
};

const ORG_DEPTH = 0;
const LOCATION_DEPTH = 1;
const MILL_DEPTH = 2;

export const extractOrganizationFromNodeId = _.memoize((nodeId: string) => extractNodeId(nodeId, ORG_DEPTH));
export const extractLocationFromNodeId = _.memoize((nodeId: string) => extractNodeId(nodeId, LOCATION_DEPTH));

/*
 * Eventually, this will need to be refactored to use the node hierarchy if we want to support node ids as guids.
 */
export const extractMillFromNodeId = _.memoize((nodeId: string) => extractNodeId(nodeId, MILL_DEPTH));

export type INodeInfo = Pick<INode, "id" | "templateName" | "parentId" | "order" | "deviceId">;

export type INodeInfoAndHierarchy = IBasicNode & {
    childrenIds: string[];
    descendantIds: string[];
};

export type INodesInfoAndHierarchy = Record<string, INodeInfoAndHierarchy>;